// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@eisinc/App/Components/Common/FancyScroll";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import DataTable from "@eisinc/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@eisinc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@eisinc/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead from "@eisinc/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@eisinc/MobiusOverrides/DataTable/DataTableRow";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import ProductBrand, { ProductBrandStyles } from "@eisinc/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import ProductPartNumbers from "@eisinc/Overrides/Components/ProductPartNumbers";
import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getWishList } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import updateGetWishListsParameter from "@insite/client-framework/Store/Components/AddToListModal/Handlers/UpdateGetWishListsParameter";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishList";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import ScheduleReminderModal, {
    ScheduleReminderModalStyles,
} from "@insite/content-library/Components/ScheduleReminderModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: AddToListModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    return {
        session: state.context.session,
        modalIsOpen: state.components.addToListModal.isOpen,
        productInfos: state.components.addToListModal.productInfos,
        getWishListsParameter: state.components.addToListModal.getWishListsParameter,
        wishListsDataView: getWishListsDataView(state, state.components.addToListModal.getWishListsParameter),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        wishListSettings: getSettingsCollection(state).wishListSettings,
        // XNG-Changes
        products: getCurrentCartState(state).value?.cartLines,
        showProductsSelection: (state as CustomAppState).xCommon.showProductsSelection,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
    loadWishList,
};

export interface AddToListModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    signInMessageText?: TypographyProps;
    descriptionText?: TypographyProps;
    listsSelect?: DynamicDropdownPresentationProps;
    newListInput?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    scheduleReminderCheckbox?: CheckboxPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
    // XNG-Changes
    productDataTableCell?: DataTableCellProps;
    productCheckboxDataTableCell?: DataTableCellProps;
    productInfoWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productImage?: LazyImageProps;
    productImageAndInfoWrapper?: InjectableCss;
    selectAllProductsCheckbox?: CheckboxPresentationProps;
    productDataTableWrapper?: InjectableCss;
    productsSelectionWrapper?: InjectableCss;
    dataTableHeaderStyles?: DataTableHeaderPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    dataTableRow?: DataTableRowProps;
}

export const addToListModalStyles: AddToListModalStyles = {
    modal: {
        size: 350,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    descriptionText: {
        as: "p",
    },
    listsSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
        },
    },
    newListInput: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
        },
    },
    scheduleReminderCheckbox: {
        css: css`
            margin-top: 15px;
            align-items: flex-start;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    addButton: {
        css: css`
            margin-left: 10px;
        `,
    },
};

export const LastUpdatedListIdCookieName = "LastUpdatedListId";

const AddToListModal = ({
    productInfos,
    session,
    modalIsOpen,
    getWishListsParameter,
    wishListsDataView,
    signInUrl,
    location,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
    loadWishList,
    extendedStyles,
    // XNG-Changes
    products,
    showProductsSelection,
    isMobileScreen,
    allowMultipleWishLists,
}: Props) => {
    const [styles] = useState(() => mergeToNew(addToListModalStyles, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [selectedWishListId, setSelectedWishListId] = useState("new");
    const [newListName, setNewListName] = useState("");
    const [selectListError, setSelectListError] = useState<React.ReactNode>("");
    const [newListNameError, setNewListNameError] = useState<React.ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [inProgress, setInProgress] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleReminderModalIsOpen, setScheduleReminderModalIsOpen] = useState(false);
    const [currentWishList, setCurrentWishList] = useState<WishListModel>();

    // <XNG-Changes>
    const [selectedProductsSelection, setSelectedProductsSelection] = useState<
        { productId?: string | null; unitOfMeasure?: string }[]
    >([]);

    // </XNG-Changes>

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }

        const lastUpdatedListId = getCookie(LastUpdatedListIdCookieName) || "new";
        setSelectedWishListId(lastUpdatedListId);
        if (
            isAuthenticated &&
            lastUpdatedListId !== "new" &&
            (!wishListsDataView.value || !wishListsDataView.value.find(o => o.id === lastUpdatedListId)) &&
            (!currentWishList || currentWishList.id !== lastUpdatedListId)
        ) {
            loadWishList({
                wishListId: lastUpdatedListId,
                onComplete: ({ apiResult }) => {
                    setCurrentWishList(apiResult);

                    if (!apiResult) {
                        setSelectedWishListId("new");
                        removeCookie(LastUpdatedListIdCookieName);
                    }
                },
            });
        }
    }, [modalIsOpen]);

    useEffect(() => {
        if (modalIsOpen && !wishListsDataView.value && !wishListsDataView.isLoading) {
            loadWishLists(getWishListsParameter);
        }
    }, [getWishListsParameter]);

    useEffect(() => {
        let options: OptionObject[] = [{ optionText: translate("Create New List"), optionValue: "new" }];
        if (wishListsDataView.value) {
            options = options.concat(
                wishListsDataView.value
                    .filter(o => !o.isAutogenerated)
                    .map(o => ({ optionText: o.name, optionValue: o.id })),
            );
        }
        if (currentWishList && !options.find(o => o.optionValue === currentWishList.id)) {
            options.push({ optionText: currentWishList.name, optionValue: currentWishList.id });
        }
        setOptions(options);
    }, [wishListsDataView, currentWishList]);

    if (!productInfos) {
        return null;
    }

    const modalCloseHandler = () => {
        setNewListName("");
        setSelectListError("");
        setNewListNameError("");
        setInProgress(false);

        setAddToListModalIsOpen({ modalIsOpen: false });
        // XNG-Changes
        setSelectedProductsSelection([]);
    };

    const listChangeHandler = (value?: string) => {
        setCurrentWishList(wishListsDataView.value?.find(o => o.id === value));
        setSelectedWishListId(value || "new");
        setSelectListError("");
    };

    const debouncedUpdateParameter = debounce((value: string) => {
        updateGetWishListsParameter({
            ...getWishListsParameter,
            query: value || undefined,
        });
    }, 200);

    const listsInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedUpdateParameter(event.currentTarget.value);
    };

    const onClosingDropdownHandler = () => {
        if (selectedWishListId !== currentWishList?.id) {
            setCurrentWishList(undefined);
        }
    };

    const newListInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewListName(event.target.value);
        let error = "";
        if (!event.target.value) {
            error = siteMessage("Lists_Enter_New_Wishlist_Name") as string;
        } else if (event.target.value.length > 100) {
            error = siteMessage("Lists_List_Name_Too_Long", "100") as string;
        }
        setNewListNameError(error);
    };

    const scheduleReminderChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsScheduled(!isScheduled);
    };

    const closeScheduleReminderModalHandler = () => {
        setScheduleReminderModalIsOpen(false);
        setIsScheduled(false);
        modalCloseHandler();
    };

    const addToListButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        const isNewList = selectedWishListId === "new";

        // <XNG-Changes>
        const selectedProducts = showProductsSelection
            ? productInfos.filter(pi =>
                  selectedProductsSelection.some(
                      s => s.productId === pi.productId && s.unitOfMeasure === pi.unitOfMeasure,
                  ),
              )
            : productInfos;

        if (!allowMultipleWishLists) {
            setInProgress(true);
            addToWishList({
                productInfos: selectedProducts,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                    modalCloseHandler();
                },
                onError: errorMessage => {
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                    setInProgress(false);
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }
        // </XNG-Changes>

        if (!currentWishList && !isNewList) {
            setSelectListError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }

        if (isNewList && !newListName) {
            setNewListNameError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }

        if (isNewList && newListName && newListName.length > 100) {
            setNewListNameError(siteMessage("Lists_List_Name_Too_Long", "100"));
            return;
        }

        setNewListNameError("");
        setSelectListError("");

        setInProgress(true);

        addToWishList({
            // XNG-Changes
            productInfos: selectedProducts,
            selectedWishList: isNewList ? undefined : currentWishList,
            newListName,
            onSuccess: async (wishList: WishListModel) => {
                setCookie(LastUpdatedListIdCookieName, wishList.id);
                setSelectedWishListId(wishList.id);
                setCurrentWishList(wishList);
                // <XNG-Changes>
                setNewListName("");
                // </XNG-Changes>
                toasterContext.addToast({
                    body: siteMessage("Lists_ProductAdded"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
                if (isScheduled) {
                    const updatedWishList = await getWishList({ wishListId: wishList.id, expand: ["schedule"] });
                    setCurrentWishList(updatedWishList);
                    setScheduleReminderModalIsOpen(true);
                } else {
                    modalCloseHandler();
                }
            },
            onError: errorMessage => {
                setInProgress(false);

                if (isNewList) {
                    setNewListNameError(errorMessage);
                } else {
                    modalCloseHandler();
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                }
            },
            onComplete(wishListProps) {
                if (wishListProps?.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.(wishListProps?.result?.wishList);
                } else if (wishListProps?.result?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onError?.(wishListProps?.result?.errorMessage);
                }
            },
        });
    };

    const returnUrl = location.pathname + location.search;

    const refElement = React.createRef<HTMLDivElement>();
    return (
        <Modal
            {...styles.modal}
            headline={siteMessage("Custom_Add_To_List_Modal_Header")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
            // XNG-Changes
            sizeVariant="small"
        >
            <StyledWrapper {...styles.innerWrapper} data-test-selector="productAddToListModal">
                {!isAuthenticated && (
                    <Typography data-test-selector="productAddToListModal_requireSignIn" {...styles.signInMessageText}>
                        {siteMessage(
                            "Lists_Must_Sign_In_Spire",
                            `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`,
                        )}
                    </Typography>
                )}
                {isAuthenticated && (
                    <>
                        {allowMultipleWishLists && (
                            <>
                                <Typography {...styles.descriptionText}>
                                    {translate("Add items to a new or existing list.")}
                                </Typography>
                                <DynamicDropdown
                                    label={translate("Select List")}
                                    {...styles.listsSelect}
                                    isLoading={wishListsDataView.isLoading}
                                    onInputChange={listsInputChangeHandler}
                                    onSelectionChange={listChangeHandler}
                                    onClose={onClosingDropdownHandler}
                                    selected={selectedWishListId}
                                    options={options}
                                    error={selectListError}
                                    data-test-selector="productAddToListModal_listSelect"
                                />
                                {selectedWishListId === "new" && (
                                    <TextField
                                        label={translate("New List Name")}
                                        value={newListName}
                                        error={newListNameError}
                                        onChange={newListInputChangeHandler}
                                        {...styles.newListInput}
                                        data-test-selector="productAddToListModal_newName"
                                    />
                                )}
                                {wishListSettings.enableWishListReminders && (
                                    <Checkbox
                                        {...styles.scheduleReminderCheckbox}
                                        checked={isScheduled}
                                        onChange={scheduleReminderChangeHandler}
                                    >
                                        {siteMessage("Lists_Schedule_Reminders")}
                                    </Checkbox>
                                )}
                            </>
                        )}
                        {showProductsSelection && (
                            <StyledWrapper {...styles.productsSelectionWrapper}>
                                <Checkbox
                                    {...styles.selectAllProductsCheckbox}
                                    {...{ allowMultipleWishLists }}
                                    checked={selectedProductsSelection.length === products?.length}
                                    onChange={() => {
                                        if (selectedProductsSelection.length === products?.length) {
                                            setSelectedProductsSelection([]);
                                        } else {
                                            setSelectedProductsSelection(
                                                products?.map(p => ({
                                                    productId: p.productId,
                                                    unitOfMeasure: p.unitOfMeasure,
                                                })) || [],
                                            );
                                        }
                                    }}
                                >
                                    {translate(`Select All (${products?.length} Products)`)}
                                </Checkbox>

                                <FancyScroll refElement={refElement}>
                                    <StyledWrapper
                                        ref={refElement}
                                        {...styles.productDataTableWrapper}
                                        {...{ allowMultipleWishLists, selectedWishListId }}
                                    >
                                        <DataTable>
                                            <DataTableHead>
                                                {!isMobileScreen && (
                                                    <DataTableHeader
                                                        {...styles.dataTableHeaderStyles}
                                                        data-test-selector="tableHeader_select"
                                                    ></DataTableHeader>
                                                )}
                                                <DataTableHeader
                                                    {...styles.dataTableHeaderStyles}
                                                    data-test-selector="tableHeader_products"
                                                >
                                                    {translate("Products")}
                                                </DataTableHeader>
                                            </DataTableHead>
                                            <DataTableBody data-test-selector="productsTable_tableBody">
                                                {products?.map(product => {
                                                    const isSelected = selectedProductsSelection.some(
                                                        s =>
                                                            s.productId === product.productId &&
                                                            s.unitOfMeasure === product.unitOfMeasure,
                                                    );
                                                    return (
                                                        <DataTableRow {...styles.dataTableRow} key={product.id}>
                                                            {!isMobileScreen && (
                                                                <DataTableCell
                                                                    {...styles.productCheckboxDataTableCell}
                                                                    data-test-selector="productsTable_tableCell_select"
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onChange={() => {
                                                                            setSelectedProductsSelection(prev =>
                                                                                isSelected
                                                                                    ? prev.filter(
                                                                                          s =>
                                                                                              s.productId !==
                                                                                                  product.productId ||
                                                                                              s.unitOfMeasure !==
                                                                                                  product.unitOfMeasure,
                                                                                      )
                                                                                    : [
                                                                                          ...prev,
                                                                                          {
                                                                                              productId:
                                                                                                  product.productId,
                                                                                              unitOfMeasure:
                                                                                                  product.unitOfMeasure,
                                                                                          },
                                                                                      ],
                                                                            );
                                                                        }}
                                                                    />
                                                                </DataTableCell>
                                                            )}
                                                            <DataTableCell
                                                                {...styles.productDataTableCell}
                                                                data-test-selector="productsTable_tableCell_products"
                                                            >
                                                                {isMobileScreen && (
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onChange={() => {
                                                                            setSelectedProductsSelection(prev =>
                                                                                isSelected
                                                                                    ? prev.filter(
                                                                                          s =>
                                                                                              s.productId !==
                                                                                                  product.productId ||
                                                                                              s.unitOfMeasure !==
                                                                                                  product.unitOfMeasure,
                                                                                      )
                                                                                    : [
                                                                                          ...prev,
                                                                                          {
                                                                                              productId:
                                                                                                  product.productId,
                                                                                              unitOfMeasure:
                                                                                                  product.unitOfMeasure,
                                                                                          },
                                                                                      ],
                                                                            );
                                                                        }}
                                                                    >
                                                                        {translate("Add to List")}
                                                                    </Checkbox>
                                                                )}
                                                                <StyledWrapper {...styles.productImageAndInfoWrapper}>
                                                                    <LazyImage
                                                                        src={product.smallImagePath}
                                                                        {...styles.productImage}
                                                                    />
                                                                    <StyledWrapper {...styles.productInfoWrapper}>
                                                                        <ProductDescription
                                                                            product={product}
                                                                            extendedStyles={
                                                                                styles.productDescriptionStyles
                                                                            }
                                                                        />
                                                                        <ProductPartNumbers
                                                                            productNumber={product.erpNumber}
                                                                            showProductNumber={true}
                                                                            manufacturerItem={product.manufacturerItem}
                                                                            showLabel={true}
                                                                        />
                                                                        {product.brand && (
                                                                            <ProductBrand
                                                                                brand={product.brand}
                                                                                showLabel={true}
                                                                                extendedStyles={
                                                                                    styles.productBrandStyles
                                                                                }
                                                                            />
                                                                        )}
                                                                    </StyledWrapper>
                                                                </StyledWrapper>
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    );
                                                })}
                                            </DataTableBody>
                                        </DataTable>
                                    </StyledWrapper>
                                </FancyScroll>
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.buttonsWrapper} {...{ showProductsSelection }}>
                            <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                                {translate("Cancel")}
                            </Button>
                            <Button
                                {...styles.addButton}
                                onClick={e => addToListButtonClickHandler(e)}
                                data-test-selector="productAddToListModal_addButton"
                                disabled={
                                    inProgress || (showProductsSelection && selectedProductsSelection.length === 0)
                                }
                            >
                                {showProductsSelection ? translate("Add Products to List") : translate("Add to List")}
                            </Button>
                        </StyledWrapper>
                        {isScheduled && currentWishList && (
                            <ScheduleReminderModal
                                wishList={currentWishList}
                                isOpen={scheduleReminderModalIsOpen}
                                handleClose={closeScheduleReminderModalHandler}
                                extendedStyles={styles.scheduleReminderModal}
                            />
                        )}
                    </>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AddToListModal);
